import React from 'react';
import './TaskSummaryStats.css';

/**
 * Shared task summary widget — scope / in-flight / processed / errors / total / progress.
 * Used by the task configuration page (clickable rows via onScopeClick) and the extraction
 * task list cards (read-only). Single source of truth so both stay visually identical.
 *
 * Props:
 *   summary       – array of { scopeType, scopeDescription, inFlightRecords, processedRecords, warningRecords, errorRecords, skippedRecords, totalRecords }
 *   onScopeClick  – optional (scopeType) => void; when provided rows become clickable
 *   compact       – optional; tighter padding for inline use in list cards
 */
export default function TaskSummaryStats({ summary, onScopeClick, compact = false }) {
    if (!Array.isArray(summary) || summary.length === 0) return null;
    const clickable = typeof onScopeClick === 'function';

    return (
        <div className={`tss-wrapper${compact ? ' tss-compact' : ''}`}>
            <table className="tss-table">
                <thead>
                    <tr>
                        <th>Scope</th>
                        <th className="tss-right">In-Flight</th>
                        <th className="tss-right">Processed</th>
                        {/* Synced, but the target rejected part of the record. Its own column because it
                            is neither a clean success nor a failure to reprocess. */}
                        <th className="tss-right" title="Synced to the target, but part of the record was rejected. Needs a configuration fix, not a reprocess.">Warnings</th>
                        <th className="tss-right">Errors</th>
                        <th className="tss-right">Skipped</th>
                        <th className="tss-right">Total</th>
                        <th>Progress</th>
                    </tr>
                </thead>
                <tbody>
                    {summary.map((item, index) => {
                        const total = parseInt(item.totalRecords) || 0;
                        const inFlight = parseInt(item.inFlightRecords) || 0;
                        const processed = parseInt(item.processedRecords) || 0;
                        const warnings = parseInt(item.warningRecords) || 0;
                        const errors = parseInt(item.errorRecords) || 0;
                        const skipped = parseInt(item.skippedRecords) || 0;
                        // A warned record DID reach the target, so it counts as done here — otherwise a task whose records
                        // all carry a rejection would sit at 0% for ever despite having fully synced.
                        const percentage = total > 0 ? Math.round(((processed + warnings) / total) * 100) : 0;

                        return (
                            <tr
                                key={index}
                                className={clickable ? 'tss-row-clickable' : undefined}
                                onClick={clickable ? (e) => { e.stopPropagation(); onScopeClick(item.scopeType || ''); } : undefined}
                                title={clickable ? 'Click to view records' : undefined}
                            >
                                <td>
                                    <div className="tss-scope-cell">
                                        <span className="tss-scope-type">{item.scopeType}</span>
                                        {item.scopeDescription && (
                                            <span className="tss-scope-desc">{item.scopeDescription}</span>
                                        )}
                                    </div>
                                </td>
                                <td className="tss-right">
                                    {inFlight > 0
                                        ? <span className="tss-error-count">{inFlight.toLocaleString()}</span>
                                        : 0}
                                </td>
                                <td className="tss-right">{processed.toLocaleString()}</td>
                                <td className="tss-right">
                                    <span className={warnings > 0 ? 'tss-warning-count' : ''}>
                                        {warnings.toLocaleString()}
                                    </span>
                                </td>
                                <td className="tss-right">
                                    <span className={errors > 0 ? 'tss-error-count' : ''}>
                                        {errors.toLocaleString()}
                                    </span>
                                </td>
                                <td className="tss-right tss-skipped-count">{skipped.toLocaleString()}</td>
                                <td className="tss-right">{total.toLocaleString()}</td>
                                <td>
                                    <div className="tss-progress-container">
                                        <div className="tss-progress-track">
                                            <div className="tss-progress-bar" style={{ width: `${percentage}%` }} />
                                        </div>
                                        <span className="tss-progress-text">{percentage}%</span>
                                    </div>
                                </td>
                            </tr>
                        );
                    })}
                </tbody>
            </table>
        </div>
    );
}
